/** @jsx jsx */
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import defaultsDeep from 'lodash/defaultsDeep';
import Swiper from 'react-id-swiper';
import Spacer from '../../ui/Spacer';
import DotComponent from '../../ui/DotComponent';
import { arrows } from '../../ui/CtaLink/types';
import { Container } from '../../../styles/Grid';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { fieldHasValue } from '../../../utils/sitecore';
import { Breakpoints, GridConfig } from '../../../styles/Grid/GridSizes';
import CtaLink from '../../ui/CtaLink';
import ArrowButtons from '../../ui/SwiperComponents/ArrowButtons';
import Picture from '../../ui/Picture';
import { CLOUDINARY_BASE_VALUES } from '../../../utils/constants';
import * as dotsStyles from '../../ui/SwiperComponents/Dots/index.styles';
import * as styles from './index.styles';

// According to the documentation, the breakpoints work mobile first, but only applies
// to Swiper@5.3.0. Breakpoints here are set desktop first.
const swiperBreakpoints = {
  sm: Breakpoints.md - 1,
  md: Breakpoints.lg - 1,
};

const defaultData = {
  Headline: {
    value: '',
  },
};

const featureIconSizes = {
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 30,
    height: 30,
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 30,
    height: 30,
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 30,
    height: 30,
  },
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 30,
    height: 30,
  },
};

export const swiperParams = {
  breakpoints: {
    [swiperBreakpoints.sm]: {
      slidesPerView: 1,
      spaceBetween: GridConfig.sm.gutter,
    },
    [swiperBreakpoints.md]: {
      slidesPerView: 2,
      spaceBetween: GridConfig.md.gutter,
    },
  },
  // lg - xl
  slidesPerView: 3,
  spaceBetween: GridConfig.lg.gutter,
  watchOverflow: true,
  pagination: {
    el: '.key-features-swiper-pagination',
    type: 'bullets',
    clickable: true,
  },
};

export class KeyFeatures extends SafePureComponent {
  state = {
    showArrows: false,
  };
  fields = defaultsDeep(this.props.fields, defaultData);
  swiper = null;

  componentDidMount() {
    this.toggleArrowsVisibility();
    window.addEventListener('resize', this.toggleArrowsVisibility);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.toggleArrowsVisibility);
  }

  toggleArrowsVisibility = debounce(() => {
    if (this.swiper) {
      this.setState({ showArrows: !this.swiper.isLocked });
    }
  }, 100);

  setSwiper = node => {
    if (node) {
      this.swiper = node.swiper;
    }
  };

  goPrev = () => {
    this.swiper && this.swiper.slidePrev();
  };

  goNext = () => {
    this.swiper && this.swiper.slideNext();
  };

  renderFeature = (props, index, componentName) => {
    const CTA = get(props, 'CTA');
    const hasCTA = !!get(CTA, 'value.text');
    return (
      <div css={styles.featureContainer} key={`feature-${index}`}>
        <Picture
          image={get(props, 'Icon')}
          imageSizes={featureIconSizes}
          instantLoad
        />
        <h4 css={styles.featureTitle}>{get(props, 'Title')}</h4>
        <div css={styles.featureDescription}>{get(props, 'Description')}</div>
        {hasCTA && (
          <CtaLink cta={CTA} componentName={componentName} isGTMTrackable />
        )}
      </div>
    );
  };

  renderModuleCTA(componentName) {
    const { ModuleCTA } = this.fields;
    const hasModuleCTA = !!get(ModuleCTA, 'value.text');
    if (hasModuleCTA) {
      return (
        <CtaLink
          cta={ModuleCTA}
          arrow={arrows.right}
          linkCustomCss={styles.moduleCTA}
          componentName={componentName}
          isGTMTrackable
        />
      );
    }
  }

  safeRender() {
    const { fields } = this;
    const { showArrows } = this.state;
    const componentName = get(this.props, 'rendering.componentName');
    const features = get(fields, 'Features.value', []);
    const renderFeatures = features && features.length > 0;

    return (
      <Spacer marginCss={styles.spacerMargin}>
        <Container>
          <div css={styles.container}>
            {fieldHasValue(fields.Headline) && (
              <h2 data-testid="title" css={styles.headline}>
                {get(fields, 'Headline.value', '')}
                <DotComponent />
              </h2>
            )}
            <div css={[styles.swiperContainer, dotsStyles.swiperContainer]}>
              <Swiper
                data-testid="features-swiper"
                {...swiperParams}
                ref={this.setSwiper}
              >
                {renderFeatures &&
                  features.map((feature, index) =>
                    this.renderFeature(feature, index, componentName),
                  )}
              </Swiper>
              {showArrows && (
                <ArrowButtons
                  innerCss={styles.arrowButtons}
                  onPrev={this.goPrev}
                  onNext={this.goNext}
                />
              )}
            </div>
          </div>
          {this.renderModuleCTA(componentName)}
        </Container>
      </Spacer>
    );
  }
}

KeyFeatures.displayName = 'KeyFeatures';

KeyFeatures.defaultProps = {
  fields: defaultData,
  t: key => key,
};

KeyFeatures.propTypes = {
  fields: PropTypes.any,
};

export default KeyFeatures;
